<template>
    <div class="pc">

        <!-- ════════════════════ DASHBOARD HEADER ════════════════════ -->
        <div class="pc-dash-header">
            <div class="pc-dash-header-top">
                <div>
                    <div class="pc-eyebrow">TALLER · CUTTING PROGRESS</div>
                    <h1 class="pc-title">Progreso de corte</h1>
                </div>
                <div class="pc-header-actions">
                    <div class="pc-stat-pill">
                        <i class="fa-solid fa-list-check"></i>
                        <strong>{{ listadoPedidos.length }}</strong>
                        <span>pedidos</span>
                    </div>
                    <div class="pc-stat-pill pc-stat-pill--cta">
                        <i class="fa-solid fa-scissors"></i>
                        <strong>{{ totalCortePendiente }}</strong>
                        <span>uds por cortar</span>
                    </div>
                    <div class="pc-stat-pill pc-stat-pill--success" v-if="pedidosListos > 0">
                        <i class="fa-solid fa-check-double"></i>
                        <strong>{{ pedidosListos }}</strong>
                        <span>listos</span>
                    </div>
                    <button
                        v-if="itemsPausados.length > 0"
                        class="pc-stat-pill pc-stat-pill--paused"
                        @click="showPausados = !showPausados"
                    >
                        <i class="fa-solid fa-pause"></i>
                        <strong>{{ itemsPausados.length }}</strong>
                        <span>pausados</span>
                        <i :class="showPausados ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" style="font-size: 10px; margin-left: 2px;"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Panel de pausados -->
        <div v-if="showPausados && itemsPausados.length > 0" class="paused-panel">
            <div class="paused-panel__header">
                <h4 class="paused-panel__title"><i class="fa-solid fa-pause"></i> Productos pausados</h4>
                <button class="paused-panel__close" @click="showPausados = false"><i class="fa-solid fa-xmark"></i></button>
            </div>
            <div class="paused-panel__list">
                <div v-for="item in itemsPausados" :key="item.id" class="paused-item">
                    <div class="paused-item__info">
                        <div class="paused-item__top">
                            <span class="paused-item__sku">{{ item.sku }}</span>
                            <span class="paused-item__pedido">{{ item.pedidoCodigo }}</span>
                            <span class="paused-item__date" v-if="item.pausedAt">{{ formatFechaPausa(item.pausedAt) }}</span>
                        </div>
                        <div class="paused-item__note" v-if="editingNoteId !== item.id">
                            <i class="fa-regular fa-comment"></i>
                            <span v-if="item.pausedNote">{{ item.pausedNote }}</span>
                            <span v-else class="paused-item__note-empty">Sin nota</span>
                            <button class="paused-item__note-edit" @click="startEditNote(item)" title="Editar nota">
                                <i class="fa-solid fa-pen"></i>
                            </button>
                        </div>
                        <div v-else class="paused-item__note-edit-row">
                            <input
                                v-model="editingNoteText"
                                class="paused-item__note-input"
                                placeholder="Motivo de la pausa"
                                @keyup.enter="guardarNota(item)"
                                @keyup.esc="cancelarEditNota"
                                ref="editNoteInput"
                            />
                            <button class="paused-item__note-save" @click="guardarNota(item)" :disabled="savingNote">
                                <i class="fa-solid fa-check"></i>
                            </button>
                            <button class="paused-item__note-cancel" @click="cancelarEditNota" :disabled="savingNote">
                                <i class="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                    </div>
                    <div class="paused-item__stats">
                        <span class="paused-item__progress">{{ item.cantidadCorte }}/{{ item.cantidadTotal }}</span>
                    </div>
                    <button class="paused-item__resume" @click="reanudarItemDirecto(item)">
                        <i class="fa-solid fa-play"></i> Reanudar
                    </button>
                </div>
            </div>
        </div>

        <!-- ════ Buscador: en qué pedidos falta un producto ════ -->
        <div class="pc-buscador">
            <div class="pc-search-box">
                <i class="fa-solid fa-magnifying-glass pc-search-icon"></i>
                <b-form-input
                    v-model="busquedaProducto"
                    placeholder="Buscar producto por nombre o SKU…"
                    class="pc-search-input"
                />
                <i v-if="busquedaProducto" class="fa-solid fa-xmark pc-search-clear" @click="busquedaProducto = ''"></i>
            </div>

            <div v-if="busquedaProducto.trim()" class="pc-resultados">
                <div v-if="resultadosBusqueda.length === 0" class="pc-resultados-vacio">
                    Ningún pedido pendiente con ese producto.
                </div>
                <div
                    v-for="r in resultadosBusqueda"
                    :key="r.pedido.id"
                    class="pc-resultado-card"
                    @click="seleccionarPedido(r.pedido)"
                >
                    <div class="pc-resultado-head">
                        <strong>{{ r.pedido.codigo }}</strong>
                        <span class="pc-resultado-falta">{{ r.faltaTotal }} uds faltan</span>
                    </div>
                    <div class="pc-resultado-items">
                        <div v-for="it in r.items" :key="it.sku" class="pc-resultado-item">
                            <span class="pc-item-sku">{{ it.sku }}</span>
                            <span class="pc-item-badge">
                                faltan <strong>{{ it.total - it.corte }}</strong>
                                <span class="pc-item-frac">({{ it.corte }}/{{ it.total }})</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <b-card-code id="card-progreso">

            <!-- Tarjetas de Pedidos rediseñadas -->
            <div class="pedidos-cards mb-3">
                <div
                    v-for="pedido in listadoPedidos"
                    :key="pedido.id"
                    class="pedido-card"
                    :class="{
                        'pedido-card-selected': pedidoSeleccionado && pedidoSeleccionado.id === pedido.id,
                        'pedido-card-listo': getVariantEstadoCorte(pedido) === 'success'
                    }"
                    @click="seleccionarPedido(pedido)"
                >
                    <div class="pedido-card-head">
                        <div class="pedido-card-codigo">
                            {{ pedido.codigo }}
                            <button
                                v-if="pedido.comentarios"
                                class="pedido-card-comentario"
                                @click.stop="verComentario(pedido)"
                                v-b-tooltip.hover.top
                                title="Ver comentario"
                            >
                                <i class="fa-solid fa-comment-dots"></i>
                            </button>
                        </div>
                        <span class="pedido-card-estado-pill" :class="'pcs-' + getVariantEstadoCorte(pedido)">
                            <i class="fa-solid fa-circle"></i>
                            {{ getEstadoCorte(pedido) }}
                        </span>
                    </div>

                    <div class="pedido-card-meta">
                        <span><i class="fa-regular fa-calendar"></i> {{ pedido.fecha }}</span>
                        <span class="pedido-card-meta-sep">·</span>
                        <span class="pedido-card-age" :class="ageClass(pedido.fecha)">{{ formatFechaRelativa(pedido.fecha) }}</span>
                    </div>

                    <div class="pedido-card-stats">
                        <div class="pedido-stat">
                            <div class="pedido-stat-label">REFS</div>
                            <div class="pedido-stat-value">{{ pedidoStats(pedido).refs }}</div>
                        </div>
                        <div class="pedido-stat">
                            <div class="pedido-stat-label">UDS</div>
                            <div class="pedido-stat-value">{{ pedidoStats(pedido).total }}</div>
                        </div>
                        <div class="pedido-stat">
                            <div class="pedido-stat-label">CORTADO</div>
                            <div class="pedido-stat-value pedido-stat-value--cta">{{ pedidoStats(pedido).corte }}</div>
                        </div>
                    </div>

                    <div class="pedido-card-progress">
                        <div class="pedido-progress-track">
                            <div class="pedido-progress-fill" :style="{ width: pedidoStats(pedido).pct + '%' }"></div>
                        </div>
                        <span class="pedido-progress-pct">{{ pedidoStats(pedido).pct }}%</span>
                    </div>
                </div>
            </div>

            <!-- Comentario del pedido abierto -->
            <div
                v-if="pedidoSeleccionado && pedidoSeleccionado.comentarios"
                class="pedido-comentario-banner"
            >
                <i class="fa-solid fa-comment-dots"></i>
                <span class="pedido-comentario-banner__text">{{ pedidoSeleccionado.comentarios }}</span>
            </div>

            <!-- Tabla de Items por Color -->
            <b-table
                v-if="itemsAgrupados.length > 0"
                :items="itemsAgrupados"
                :fields="columnasTable"
                class="table-borderless table-progreso table-ultra-compact"
                small
                striped
            >
                <template #cell(sku)="data">
                    <strong>{{data.item.sku }}</strong>
                </template>

                <template v-for="columna in columnasTable.filter((f) => f.key.startsWith('corte_'))"
                    #[`cell(${columna.key})`]="data">
                    <div :key="columna.key" class="cell-progreso" :class="data.item[columna.key.replace('corte_', 'listo_')] ? 'is-terminado' : 'is-pendiente'" v-if="data.item[columna.key.replace('corte_', 'id_')]">
                        <div class="progreso-info">
                            <span class="progreso-actual">{{ data.item[columna.key] || 0 }}</span>
                            <span class="progreso-total">/ {{ data.item[columna.key.replace('corte_', 'total_')] }}</span>
                            <i
                                v-if="data.item[columna.key] > 0"
                                class="fa-solid fa-pen btn-editar-corte"
                                title="Corregir cantidad cortada"
                                @click="editarCorte(data.item, columna.key)"
                            ></i>
                        </div>
                        <b-progress
                            :value="data.item[columna.key] || 0"
                            :max="data.item[columna.key.replace('corte_', 'total_')]"
                            height="4px"
                            class="mb-1"
                            variant="info"
                        ></b-progress>
                        <div v-if="data.item[columna.key] < data.item[columna.key.replace('corte_', 'total_')]" class="input-progreso">
                            <b-form-input
                                v-model.number="data.item[columna.key.replace('corte_', 'input_')]"
                                type="number"
                                size="sm"
                                class="input-cantidad"
                                min="1"
                                placeholder="+N"
                                @wheel.prevent
                                @keyup.enter="incrementarProgreso(data.item, columna.key)"
                            />
                            <span
                                class="btn-incrementar"
                                @click="incrementarProgreso(data.item, columna.key)"
                            >
                                <i class="fa-solid fa-plus"></i>
                            </span>
                        </div>
                        <div v-else class="text-success text-center">
                            <i class="fa-solid fa-check"></i>
                        </div>
                        <div class="pause-row">
                            <button
                                type="button"
                                class="btn-pause"
                                :class="{ 'btn-pause--active': data.item[columna.key.replace('corte_', 'paused_')] }"
                                @click="togglePausaItem(data.item, columna.key)"
                                :title="data.item[columna.key.replace('corte_', 'paused_')] ? 'Reanudar' : 'Pausar'"
                            >
                                <i :class="data.item[columna.key.replace('corte_', 'paused_')] ? 'fa-solid fa-play' : 'fa-solid fa-pause'"></i>
                            </button>
                            <span
                                v-if="data.item[columna.key.replace('corte_', 'paused_')] && data.item[columna.key.replace('corte_', 'pausedAt_')]"
                                class="paused-at"
                            >{{ formatFechaPausa(data.item[columna.key.replace('corte_', 'pausedAt_')]) }}</span>
                        </div>
                    </div>
                </template>
            </b-table>

            <div v-else-if="pedidoSeleccionado" class="text-center text-muted py-3">
                No hay items en este pedido
            </div>

            <div v-else class="text-center text-muted py-3">
                Selecciona un pedido para ver su progreso
            </div>

        </b-card-code>

        <!-- ============ MODAL NOTA DE PAUSA ============ -->
        <b-modal
            v-model="pauseModalVisible"
            title="Pausar producto"
            size="md"
            centered
            hide-footer
            @hidden="onPauseModalHidden"
        >
            <div class="pause-modal" v-if="pauseTarget">
                <div class="pause-modal__info">
                    <span class="pause-modal__sku">{{ pauseTarget.sku }}</span>
                    <span class="pause-modal__color">{{ pauseTarget.color.toUpperCase() }}</span>
                </div>
                <label class="pause-modal__label">Motivo / nota de pausa</label>
                <b-form-textarea
                    v-model="pauseNote"
                    rows="3"
                    max-rows="6"
                    placeholder="Ej: falta piel, esperando muestra, etc."
                    autofocus
                />
                <div class="pause-modal__actions">
                    <button class="pause-modal__cancel" @click="pauseModalVisible = false" :disabled="pauseSaving">Cancelar</button>
                    <button class="pause-modal__save" @click="confirmarPausa" :disabled="pauseSaving">
                        <i class="fa-solid fa-pause"></i> Pausar
                    </button>
                </div>
            </div>
        </b-modal>

        <!-- ============ MODAL VER COMENTARIO ============ -->
        <b-modal
            ref="modal-ver-comentario"
            title="Comentario del pedido"
            size="md"
            centered
            ok-only
            ok-title="Cerrar"
        >
            <div v-if="pedidoComentario">
                <p class="mb-1 text-muted">Pedido <b>{{ pedidoComentario.codigo }}</b></p>
                <div class="comentario-texto">{{ pedidoComentario.comentarios }}</div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import axios from '@axios'
import { BTable, BFormInput, BFormGroup, BBadge, BProgress, BModal, BFormTextarea } from 'bootstrap-vue'
import BCardCode from '@core/components/b-card-code'
import pedirCantidadCorregida from '@/utils/corregirCantidad'

export default {
    components: { BCardCode, BTable, BFormInput, BFormGroup, BBadge, BProgress, BModal, BFormTextarea },
    data() {
        return {
            columnasTable: [
                { key: "sku", label: "Producto", stickyColumn: true, thStyle: { width: "150px" } },
            ],
            listadoPedidos: [],
            pedidoSeleccionado: null,
            pedidoComentario: null,
            itemsPedido: [],
            itemsAgrupados: [],
            skuTitulos: {},
            busquedaProducto: '',
            coloresDisponibles: [],
            showPausados: false,
            pauseModalVisible: false,
            pauseTarget: null,
            pauseNote: '',
            pauseSaving: false,
            editingNoteId: null,
            editingNoteText: '',
            savingNote: false,
        }
    },
    computed: {
        // Pedidos donde el producto buscado aún falta por cortar (corte < total)
        resultadosBusqueda() {
            const q = this.busquedaProducto.trim().toLowerCase();
            if (!q) return [];
            const res = [];
            (this.listadoPedidos || []).forEach(pedido => {
                const items = (pedido.listaItems || [])
                    .map(i => ({
                        sku: i.sku,
                        nombre: this.getNombreProducto(i.sku),
                        total: Number(i.cantidadTotal) || 0,
                        corte: Number(i.cantidadCorte) || 0,
                    }))
                    .filter(i => i.total - i.corte > 0
                        && ((i.sku || '').toLowerCase().includes(q) || i.nombre.toLowerCase().includes(q)));
                if (items.length) {
                    const faltaTotal = items.reduce((s, i) => s + (i.total - i.corte), 0);
                    res.push({ pedido, items, faltaTotal });
                }
            });
            return res;
        },
        itemsPausados() {
            const pausados = [];
            (this.listadoPedidos || []).forEach(p => {
                if (!p.listaItems) return;
                p.listaItems.forEach(i => {
                    if (Number(i.paused) === 1) {
                        pausados.push({
                            id: i.id,
                            sku: i.sku,
                            cantidadTotal: i.cantidadTotal,
                            cantidadCorte: i.cantidadCorte,
                            pausedAt: i.pausedAt,
                            pausedNote: i.pausedNote || '',
                            pedidoCodigo: p.codigo,
                            pedidoId: p.id,
                        });
                    }
                });
            });
            return pausados;
        },
        totalCortePendiente() {
            var total = 0;
            (this.listadoPedidos || []).forEach(p => {
                if (!p.listaItems) return;
                p.listaItems.forEach(i => {
                    var falta = (Number(i.cantidadTotal) || 0) - (Number(i.cantidadCorte) || 0);
                    if (falta > 0) total += falta;
                });
            });
            return total;
        },
        pedidosListos() {
            return (this.listadoPedidos || []).filter(p => this.getVariantEstadoCorte(p) === 'success').length;
        },
    },
    methods: {
        pedidoStats(pedido) {
            if (!pedido || !pedido.listaItems || pedido.listaItems.length === 0) {
                return { refs: 0, total: 0, corte: 0, pct: 0 };
            }
            const items = pedido.listaItems;
            const total = items.reduce((s, i) => s + (Number(i.cantidadTotal) || 0), 0);
            const corte = items.reduce((s, i) => s + (Number(i.cantidadCorte) || 0), 0);
            return {
                refs: items.length,
                total: total,
                corte: corte,
                pct: total > 0 ? Math.round((corte / total) * 100) : 0,
            };
        },
        formatFechaRelativa(fecha) {
            if (!fecha) return '—';
            const d = new Date(fecha);
            if (isNaN(d.getTime())) return fecha;
            const diffDias = Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
            if (diffDias === 0) return 'hoy';
            if (diffDias === 1) return 'ayer';
            if (diffDias < 7)   return 'hace ' + diffDias + ' días';
            if (diffDias < 30)  return 'hace ' + Math.floor(diffDias / 7) + ' sem';
            if (diffDias < 365) return 'hace ' + Math.floor(diffDias / 30) + ' meses';
            return 'hace ' + Math.floor(diffDias / 365) + ' años';
        },
        ageClass(fecha) {
            if (!fecha) return '';
            const d = new Date(fecha);
            if (isNaN(d.getTime())) return '';
            const dias = Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
            if (dias < 7) return 'age-fresh';
            if (dias < 15) return 'age-warn';
            return 'age-stale';
        },
        formatFechaPausa(fecha) {
            if (!fecha) return '';
            const d = new Date(fecha);
            if (isNaN(d.getTime())) return '';
            const dia = String(d.getDate()).padStart(2, '0');
            const mes = String(d.getMonth() + 1).padStart(2, '0');
            const hora = String(d.getHours()).padStart(2, '0');
            const min = String(d.getMinutes()).padStart(2, '0');
            return `Pausado ${dia}/${mes} ${hora}:${min}`;
        },
        async loadPedidos() {
            try {
                axios.post("/uso", { seccion: "progresoCorte" });
                const resTitulos = await axios.get("/sku/title");
                resTitulos.data.forEach(item => {
                    this.skuTitulos[item.skuSingle] = item.productTitle;
                });

                const response = await axios.get("/taller/pedido/vero");
                var pedidos = response.data.filter(function(p) { return p.estado !== 'Terminado'; });

                // Cargar items de cada pedido para calcular estado de corte
                for (var i = 0; i < pedidos.length; i++) {
                    try {
                        var resItems = await axios.get('/taller/pedido/vero/items/' + pedidos[i].id);
                        pedidos[i].listaItems = resItems.data;
                    } catch (e) {
                        pedidos[i].listaItems = [];
                    }
                }

                this.listadoPedidos = pedidos;
            } catch (error) {
                console.error("Error cargando pedidos:", error);
            }
        },
        seleccionarPedido(pedido) {
            this.pedidoSeleccionado = pedido;
            this.cargarItemsPedido();
        },
        verComentario(pedido) {
            this.pedidoComentario = pedido;
            this.$refs['modal-ver-comentario'].show();
        },
        async cargarItemsPedido() {
            if (!this.pedidoSeleccionado) {
                this.itemsPedido = [];
                this.itemsAgrupados = [];
                return;
            }
            try {
                const response = await axios.get(`/taller/pedido/vero/faltantes/${this.pedidoSeleccionado.id}`);
                this.itemsPedido = response.data;
                this.agruparPorRaiz();
            } catch (error) {
                console.error("Error cargando items:", error);
            }
        },
        agruparPorRaiz() {
            const grupos = {};
            const coloresSet = new Set();

            this.itemsPedido.forEach(item => {
                const lastDash = item.sku.lastIndexOf('-');
                const raiz = item.sku.substring(0, lastDash);
                const color = item.sku.substring(lastDash + 1).toLowerCase();

                coloresSet.add(color);

                if (!grupos[raiz]) {
                    grupos[raiz] = { sku: raiz };
                }

                grupos[raiz][`id_${color}`] = item.id;
                grupos[raiz][`total_${color}`] = item.cantidadTotal;
                grupos[raiz][`corte_${color}`] = item.cantidadCorte;
                // Listo para corte: cortado entero o item marcado como terminado
                grupos[raiz][`listo_${color}`] = item.cantidadCorte >= item.cantidadTotal || Boolean(Number(item.terminado));
                grupos[raiz][`input_${color}`] = null;
                grupos[raiz][`paused_${color}`] = Boolean(Number(item.paused) || 0);
                grupos[raiz][`pausedAt_${color}`] = item.pausedAt || null;
            });

            // Actualizar columnas con orden específico
            const ordenColores = ['tan', 'black', 'blk', 'db', 'navy', 'grey', 'mocha', 'olive', 'camel', 'caoba', 'brown', 'army', 'amber', 'stone', 'hab', 'khaki', 'khk'];
            this.coloresDisponibles = Array.from(coloresSet).sort((a, b) => {
                const indexA = ordenColores.indexOf(a);
                const indexB = ordenColores.indexOf(b);
                if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            });
            this.columnasTable = [
                { key: "sku", label: "Producto", stickyColumn: true, thStyle: { width: "150px" } },
            ];

            this.coloresDisponibles.forEach(color => {
                this.columnasTable.push({
                    key: `corte_${color}`,
                    label: color.toUpperCase(),
                    thStyle: { width: "80px", textAlign: "center" }
                });
            });

            this.itemsAgrupados = Object.values(grupos);
        },
        getNombreProducto(sku) {
            const lastDash = (sku || '').lastIndexOf('-');
            const raiz = lastDash > 0 ? sku.substring(0, lastDash) : sku;
            return this.skuTitulos[raiz] || raiz;
        },
        getEstadoCorte(pedido) {
            var items = pedido.listaItems;
            if (!items || items.length === 0) return pedido.estado;
            var todoCorte = items.every(function(i) { return i.cantidadCorte >= i.cantidadTotal; });
            if (todoCorte) return 'Corte listo';
            return pedido.estado;
        },
        getVariantEstadoCorte(pedido) {
            var items = pedido.listaItems;
            if (!items || items.length === 0) return 'light';
            var todoCorte = items.every(function(i) { return i.cantidadCorte >= i.cantidadTotal; });
            if (todoCorte) return 'success';
            if (pedido.estado === 'En proceso') return 'warning';
            return 'light';
        },
        async incrementarProgreso(item, columnaKey) {
            const color = columnaKey.replace('corte_', '');
            const inputKey = `input_${color}`;
            const idKey = `id_${color}`;
            const cantidadNueva = item[inputKey];

            if (!cantidadNueva || cantidadNueva < 1) return;

            try {
                const response = await axios.put("/taller/pedido/vero/progreso", {
                    id: item[idKey],
                    columna: 'cantidadCorte',
                    cantidadNueva: cantidadNueva
                });

                this.$bvToast.toast(`+${cantidadNueva} registrado`, { title: 'Guardado', variant: 'success', solid: true, autoHideDelay: 2000 });

                item[inputKey] = null;
                await this.cargarItemsPedido();

                // Actualizar listaItems del pedido seleccionado para refrescar badge
                if (this.pedidoSeleccionado) {
                    try {
                        var resItems = await axios.get('/taller/pedido/vero/items/' + this.pedidoSeleccionado.id);
                        this.pedidoSeleccionado.listaItems = resItems.data;
                    } catch (e) {}
                }
            } catch (error) {
                console.error("Error actualizando progreso:", error);
                this.$bvToast.toast('No se pudo actualizar el progreso', { title: 'Error', variant: 'danger', solid: true });
            }
        },
        // Corrige el total cortado (corte no afecta a albarán)
        async editarCorte(item, columnaKey) {
            const color = columnaKey.replace('corte_', '');
            const actual = item[columnaKey] || 0;

            const value = await pedirCantidadCorregida(this.$swal, {
                sku: `${item.sku}-${color.toUpperCase()}`,
                label: 'Total cortado',
                actual,
                total: item[`total_${color}`] || 0,
            });
            if (value === null) return;

            try {
                await axios.put('/taller/pedido/vero/item/corregir', {
                    id: item[`id_${color}`],
                    columna: 'cantidadCorte',
                    cantidad: value,
                });

                this.$bvToast.toast(`${actual} → ${value}`, { title: 'Corregido', variant: 'success', solid: true, autoHideDelay: 2000 });

                await this.cargarItemsPedido();
                if (this.pedidoSeleccionado) {
                    try {
                        var resItems = await axios.get('/taller/pedido/vero/items/' + this.pedidoSeleccionado.id);
                        this.pedidoSeleccionado.listaItems = resItems.data;
                    } catch (e) {}
                }
            } catch (error) {
                console.error("Error corrigiendo cantidad:", error);
                this.$bvToast.toast('No se pudo corregir la cantidad', { title: 'Error', variant: 'danger', solid: true });
            }
        },
        async reanudarItemDirecto(pausedItem) {
            try {
                await axios.put("/taller/pedido/vero/item/pausa", {
                    id: pausedItem.id,
                    paused: false,
                });
                this.$bvToast.toast('Producto reanudado', { title: 'Pausa', variant: 'success', solid: true, autoHideDelay: 1800 });
                // Recargar items del pedido afectado
                const pedido = this.listadoPedidos.find(p => p.id === pausedItem.pedidoId);
                if (pedido) {
                    try {
                        var resItems = await axios.get('/taller/pedido/vero/items/' + pedido.id);
                        pedido.listaItems = resItems.data;
                    } catch (e) {}
                }
                // Recargar tabla si es el pedido seleccionado
                if (this.pedidoSeleccionado && this.pedidoSeleccionado.id === pausedItem.pedidoId) {
                    await this.cargarItemsPedido();
                }
            } catch (error) {
                console.error("Error reanudando:", error);
            }
        },
        async togglePausaItem(item, columnaKey) {
            const color = columnaKey.replace('corte_', '');
            const idKey = `id_${color}`;
            const pausedKey = `paused_${color}`;
            const nextPaused = !item[pausedKey];

            if (nextPaused) {
                this.pauseTarget = { id: item[idKey], sku: item.sku, color };
                this.pauseNote = '';
                this.pauseModalVisible = true;
                return;
            }

            await this.aplicarPausa(item[idKey], false, null);
        },
        async confirmarPausa() {
            if (!this.pauseTarget) return;
            this.pauseSaving = true;
            try {
                await this.aplicarPausa(this.pauseTarget.id, true, this.pauseNote.trim() || null);
                this.pauseModalVisible = false;
            } finally {
                this.pauseSaving = false;
            }
        },
        onPauseModalHidden() {
            this.pauseTarget = null;
            this.pauseNote = '';
        },
        startEditNote(item) {
            this.editingNoteId = item.id;
            this.editingNoteText = item.pausedNote || '';
            this.$nextTick(() => {
                const el = this.$refs.editNoteInput;
                if (el && el[0]) el[0].focus();
                else if (el && el.focus) el.focus();
            });
        },
        cancelarEditNota() {
            this.editingNoteId = null;
            this.editingNoteText = '';
        },
        async guardarNota(item) {
            this.savingNote = true;
            try {
                await axios.put("/taller/pedido/vero/item/pausa", {
                    id: item.id,
                    paused: true,
                    note: this.editingNoteText.trim() || null,
                });
                this.$bvToast.toast('Nota actualizada', { title: 'Pausa', variant: 'success', solid: true, autoHideDelay: 1500 });
                this.editingNoteId = null;
                this.editingNoteText = '';

                const pedido = this.listadoPedidos.find(p => p.id === item.pedidoId);
                if (pedido) {
                    try {
                        var resItems = await axios.get('/taller/pedido/vero/items/' + pedido.id);
                        pedido.listaItems = resItems.data;
                    } catch (e) {}
                }
                if (this.pedidoSeleccionado && this.pedidoSeleccionado.id === item.pedidoId) {
                    await this.cargarItemsPedido();
                }
            } catch (error) {
                console.error("Error guardando nota:", error);
                this.$bvToast.toast('No se pudo guardar la nota', { title: 'Error', variant: 'danger', solid: true });
            } finally {
                this.savingNote = false;
            }
        },
        async aplicarPausa(id, paused, note) {
            try {
                await axios.put("/taller/pedido/vero/item/pausa", {
                    id,
                    paused,
                    note,
                });

                this.$bvToast.toast(paused ? 'Producto pausado' : 'Producto reanudado', { title: 'Pausa', variant: 'success', solid: true, autoHideDelay: 1800 });

                await this.cargarItemsPedido();

                if (this.pedidoSeleccionado) {
                    try {
                        var resItems = await axios.get('/taller/pedido/vero/items/' + this.pedidoSeleccionado.id);
                        this.pedidoSeleccionado.listaItems = resItems.data;
                    } catch (e) {}
                }
            } catch (error) {
                console.error("Error actualizando pausa:", error);
                this.$bvToast.toast('No se pudo actualizar la pausa', { title: 'Error', variant: 'danger', solid: true });
            }
        },
    },
    mounted() {
        this.loadPedidos();
    }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

// Tokens
$pc-bg:        #F8FAFC;
$pc-surface:   #FFFFFF;
$pc-surface-2: #F1F5F9;
$pc-border:    #E2E8F0;
$pc-divider:   #EEF2F7;
$pc-ink:       #1E293B;
$pc-mute:      #64748B;
$pc-soft:      #94A3B8;
$pc-primary:   #3B82F6;
$pc-cta:       #F97316;
$pc-emerald:   #10B981;
$pc-coral:     #EF4444;
$pc-amber:     #F59E0B;

.pc {
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    background: $pc-bg;
    color: $pc-ink;
    margin: -2rem;
    padding: 28px 32px 56px;
    min-height: calc(100vh - 4rem);
    font-feature-settings: "tnum", "zero";
}

// ──────────── BUSCADOR DE PRODUCTO ────────────
.pc-buscador {
    margin-bottom: 20px;
}

.pc-search-box {
    position: relative;
    max-width: 480px;

    .pc-search-icon {
        position: absolute;
        left: 14px; top: 50%;
        transform: translateY(-50%);
        color: $pc-soft;
        pointer-events: none;
    }
    .pc-search-input {
        padding-left: 38px;
        padding-right: 34px;
        height: 44px;
        border-radius: 12px;
        border: 1px solid $pc-border;
        background: $pc-surface;
        font-weight: 600;
        &:focus { border-color: $pc-primary; box-shadow: none; }
    }
    .pc-search-clear {
        position: absolute;
        right: 14px; top: 50%;
        transform: translateY(-50%);
        color: $pc-soft;
        cursor: pointer;
        &:hover { color: $pc-coral; }
    }
}

.pc-resultados {
    margin-top: 12px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 12px;
    align-items: start;
}
.pc-resultados-vacio {
    grid-column: 1 / -1;
    color: $pc-mute;
    font-size: 0.85rem;
    padding: 8px 2px;
}
.pc-resultado-card {
    background: $pc-surface;
    border: 1px solid $pc-border;
    border-radius: 12px;
    padding: 12px 16px;
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
    &:hover {
        border-color: $pc-primary;
        box-shadow: 0 2px 10px rgba(59, 130, 246, 0.12);
    }
}
.pc-resultado-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
    strong { color: $pc-ink; font-size: 0.95rem; }
    .pc-resultado-falta {
        color: $pc-cta;
        font-weight: 700;
        font-size: 0.8rem;
    }
}
.pc-resultado-items {
    display: flex;
    flex-direction: column;
}
.pc-resultado-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 7px 0;
    font-size: 0.82rem;
    border-top: 1px solid $pc-divider;

    .pc-item-sku {
        font-weight: 600;
        color: $pc-ink;
        font-family: 'JetBrains Mono', monospace;
        letter-spacing: -0.2px;
    }
    .pc-item-badge {
        flex: 0 0 auto;
        color: $pc-cta;
        font-weight: 600;
        white-space: nowrap;
        strong { font-weight: 800; }
        .pc-item-frac { color: $pc-soft; font-weight: 500; margin-left: 2px; }
    }
}

// ──────────── DASHBOARD HEADER ────────────
.pc-dash-header {
    background: $pc-surface;
    border: 1px solid $pc-border;
    border-radius: 14px;
    padding: 18px 20px 16px;
    margin-bottom: 18px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.pc-dash-header-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
}

.pc-eyebrow {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    color: $pc-primary;
    letter-spacing: 0.14em;
    margin-bottom: 4px;

    &::before {
        content: '';
        display: inline-block;
        width: 6px;
        height: 6px;
        background: $pc-cta;
        border-radius: 50%;
        margin-right: 6px;
        vertical-align: middle;
        animation: pc-pulse 2s infinite;
    }
}

.pc-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: $pc-ink;
    line-height: 1.1;
    letter-spacing: -0.01em;
    margin: 0;
}

.pc-header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
}

.pc-stat-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 14px;
    background: $pc-bg;
    border: 1px solid $pc-border;
    border-radius: 8px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: $pc-mute;

    i { color: $pc-primary; font-size: 12px; }
    strong {
        font-family: 'JetBrains Mono', monospace;
        font-size: 13px;
        font-weight: 700;
        color: $pc-ink;
        font-feature-settings: "tnum";
    }

    &--cta {
        background: rgba(249, 115, 22, 0.06);
        border-color: rgba(249, 115, 22, 0.2);
        color: darken($pc-cta, 8%);
        i { color: $pc-cta; }
        strong { color: darken($pc-cta, 8%); }
    }
    &--success {
        background: rgba(16, 185, 129, 0.06);
        border-color: rgba(16, 185, 129, 0.2);
        color: darken($pc-emerald, 8%);
        i { color: $pc-emerald; }
        strong { color: darken($pc-emerald, 8%); }
    }
    &--paused {
        background: rgba($pc-amber, 0.06);
        border-color: rgba($pc-amber, 0.25);
        color: darken($pc-amber, 12%);
        cursor: pointer;
        transition: all 150ms ease;
        i { color: $pc-amber; }
        strong { color: darken($pc-amber, 12%); }
        &:hover { background: rgba($pc-amber, 0.12); }
    }
}

// ──────────── PAUSED PANEL ────────────
.paused-panel {
    background: $pc-surface;
    border: 1px solid rgba($pc-amber, 0.2);
    border-radius: 10px;
    margin-bottom: 16px;
    overflow: hidden;

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 18px;
        background: rgba($pc-amber, 0.04);
        border-bottom: 1px solid rgba($pc-amber, 0.12);
    }

    &__title {
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-size: 13px;
        font-weight: 700;
        color: darken($pc-amber, 12%);
        margin: 0;
        i { margin-right: 6px; }
    }

    &__close {
        width: 26px; height: 26px; border-radius: 6px; border: none;
        background: transparent; color: $pc-mute; cursor: pointer;
        display: flex; align-items: center; justify-content: center; font-size: 12px;
        &:hover { background: rgba($pc-amber, 0.1); }
    }

    &__list {
        padding: 8px 12px;
        max-height: 300px;
        overflow-y: auto;
    }
}

.paused-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 10px;
    border-radius: 8px;
    transition: background 0.12s;

    &:hover { background: rgba($pc-amber, 0.03); }

    & + & { border-top: 1px solid $pc-border; }

    &__info {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }

    &__sku {
        font-family: 'JetBrains Mono', monospace;
        font-size: 12px;
        font-weight: 600;
        color: $pc-ink;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__pedido {
        font-family: 'JetBrains Mono', monospace;
        font-size: 10px;
        font-weight: 600;
        color: $pc-primary;
        background: rgba($pc-primary, 0.08);
        padding: 2px 7px;
        border-radius: 4px;
        white-space: nowrap;
    }

    &__date {
        font-family: 'JetBrains Mono', monospace;
        font-size: 10px;
        color: $pc-soft;
        white-space: nowrap;
    }

    &__stats {
        flex-shrink: 0;
    }

    &__progress {
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px;
        font-weight: 600;
        color: $pc-mute;
        font-feature-settings: "tnum";
    }

    &__resume {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 4px 10px;
        border-radius: 6px;
        border: none;
        background: rgba($pc-emerald, 0.08);
        color: darken($pc-emerald, 5%);
        font-family: 'JetBrains Mono', monospace;
        font-size: 10px;
        font-weight: 600;
        cursor: pointer;
        transition: all 150ms;
        white-space: nowrap;
        flex-shrink: 0;

        &:hover { background: $pc-emerald; color: white; }
        i { font-size: 9px; }
    }
}

#card-progreso .card-header {
    display: none;
}

// ──────────── PEDIDOS GRID ────────────
.pedidos-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 12px;
}

.pedido-card {
    background: $pc-surface;
    border: 1px solid $pc-border;
    border-radius: 12px;
    padding: 14px 16px 12px;
    cursor: pointer;
    transition: all 200ms ease;
    display: flex;
    flex-direction: column;
    gap: 10px;

    &:hover {
        border-color: $pc-primary;
        box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
        transform: translateY(-1px);
    }

    &.pedido-card-listo { border-color: rgba(16, 185, 129, 0.4); }

    &.pedido-card-selected {
        border-color: $pc-primary;
        background: linear-gradient(180deg, rgba(59, 130, 246, 0.04) 0%, $pc-surface 100%);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
    }
}

.pedido-card-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
}

.pedido-card-codigo {
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    font-weight: 700;
    color: $pc-ink;
    letter-spacing: 0.02em;
    display: inline-flex;
    align-items: center;
    gap: 7px;
}

.pedido-card-comentario {
    border: none;
    background: rgba(245, 158, 11, 0.14);
    color: #f59e0b;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 11px;
    transition: all 0.15s ease;

    &:hover { background: #f59e0b; color: #fff; }
}

.comentario-texto {
    white-space: pre-wrap;
    word-break: break-word;
    background: #f6f7f9;
    border: 1px solid #ecedf1;
    padding: 12px 14px;
    border-radius: 8px;
    font-size: 14px;
    line-height: 1.55;
    color: $pc-ink;
}

.pedido-comentario-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 14px;
    padding: 11px 14px;
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.28);
    border-radius: 10px;

    i { color: #f59e0b; font-size: 14px; margin-top: 2px; flex-shrink: 0; }

    &__text {
        white-space: pre-wrap;
        word-break: break-word;
        font-size: 13.5px;
        line-height: 1.5;
        color: $pc-ink;
    }
}

.pedido-card-estado-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 3px 8px;
    border-radius: 999px;

    i { font-size: 5px; }

    &.pcs-success { background: rgba(16, 185, 129, 0.12); color: $pc-emerald; }
    &.pcs-warning { background: rgba(245, 158, 11, 0.12); color: darken($pc-amber, 8%); }
    &.pcs-light   { background: $pc-surface-2; color: $pc-mute; }
    &.pcs-danger  { background: rgba(239, 68, 68, 0.12); color: $pc-coral; }
}

.pedido-card-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: $pc-mute;

    i { font-size: 9px; opacity: 0.7; }

    .pedido-card-meta-sep { opacity: 0.4; }
}

.pedido-card-age {
    font-weight: 700;
    text-transform: lowercase;
    letter-spacing: 0.02em;

    &.age-fresh { color: $pc-emerald; }
    &.age-warn  { color: $pc-amber; }
    &.age-stale { color: $pc-coral; }
}

.pedido-card-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    padding: 8px 0;
    border-top: 1px solid $pc-divider;
    border-bottom: 1px solid $pc-divider;
}

.pedido-stat {
    display: flex;
    flex-direction: column;
    gap: 1px;
    text-align: center;
}

.pedido-stat-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 8.5px;
    font-weight: 600;
    color: $pc-soft;
    letter-spacing: 0.08em;
}

.pedido-stat-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 15px;
    font-weight: 700;
    color: $pc-ink;
    line-height: 1.1;
    font-feature-settings: "tnum";

    &--cta { color: $pc-cta; }
}

.pedido-card-progress {
    display: flex;
    align-items: center;
    gap: 8px;
}

.pedido-progress-track {
    flex: 1;
    height: 6px;
    background: $pc-surface-2;
    border-radius: 999px;
    overflow: hidden;
}

.pedido-progress-fill {
    height: 100%;
    background: $pc-cta;
    border-radius: 999px;
    transition: width 300ms ease;
}

.pedido-progress-pct {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    color: $pc-cta;
    width: 38px;
    text-align: right;
    font-feature-settings: "tnum";
}

@keyframes pc-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.55; transform: scale(0.85); }
}

@media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
    }
}

.table-progreso {
    font-size: 0.85rem;

    th {
        text-align: center;
        background: #f8f9fa;
        font-weight: 600;
        font-size: 11px;
    }

    td {
        vertical-align: middle;
        text-align: center;
        padding: 8px 4px !important;
    }

    td:first-child {
        text-align: left;
        padding-left: 10px !important;
    }
}

.cell-progreso {
    min-width: 70px;
    padding: 4px 6px;
    border-radius: 6px;

    // Terminado en verde, pendiente en ámbar (igual que el desglose de /pedido-taller-vero)
    &.is-terminado { background: rgba(#10B981, 0.12); }
    &.is-pendiente { background: rgba(#F59E0B, 0.12); }
}

.progreso-info {
    font-size: 12px;
    margin-bottom: 2px;
}

.progreso-actual {
    font-weight: bold;
    color: #17a2b8;
}

.progreso-total {
    color: #888;
}

.btn-editar-corte {
    margin-left: 4px;
    font-size: 10px;
    color: #94A3B8;
    cursor: pointer;
    &:hover { color: #17a2b8; }
}

.input-progreso {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 4px;
}

.input-cantidad {
    width: 45px !important;
    height: 24px !important;
    font-size: 11px !important;
    padding: 2px 4px !important;
    text-align: center;
}

.btn-incrementar {
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #17a2b8;
    color: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 10px;

    &:hover {
        background: #138496;
    }
}

.cell-paused-label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 5px 8px;
    border-radius: 8px;
    background: rgba($pc-amber, 0.12);
    color: darken($pc-amber, 10%);
    font-size: 11px;
    font-weight: 700;
}

.pause-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    margin-top: 4px;
    min-height: 18px;
}

.btn-pause {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: $pc-mute;
    border-radius: 4px;
    padding: 0;
    font-size: 8px;
    opacity: 0.3;
    transition: all 150ms ease;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        opacity: 1;
        background: rgba($pc-mute, 0.08);
    }
}

.btn-pause--active {
    opacity: 0.8;
    color: darken($pc-amber, 8%);

    &:hover {
        opacity: 1;
        background: rgba($pc-amber, 0.1);
    }
}

.paused-at {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    color: $pc-soft;
    white-space: nowrap;
}

.table-ultra-compact td {
    min-width: 70px !important;
    max-width: 90px !important;
}

.table-ultra-compact td:first-child {
    min-width: 150px !important;
    max-width: 200px !important;
}

.paused-item__info {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 4px !important;
    flex: 1;
    min-width: 0;
}
.paused-item__top {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}
.paused-item__note {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #b45309;
    line-height: 1.3;
    word-break: break-word;
}
.paused-item__note i {
    font-size: 10px;
    flex-shrink: 0;
}
.paused-item__note-empty {
    font-style: italic;
    opacity: 0.6;
}
.paused-item__note-edit {
    background: transparent;
    border: none;
    color: #b45309;
    cursor: pointer;
    padding: 0 4px;
    font-size: 10px;
    opacity: 0.6;
    transition: opacity 0.15s ease;
}
.paused-item__note-edit:hover { opacity: 1; }
.paused-item__note-edit-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
}
.paused-item__note-input {
    flex: 1;
    min-width: 180px;
    padding: 4px 8px;
    font-size: 11px;
    border: 1px solid #d8d6de;
    border-radius: 4px;
    outline: none;
}
.paused-item__note-input:focus { border-color: #7367f0; }
.paused-item__note-save,
.paused-item__note-cancel {
    border: none;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
}
.paused-item__note-save { background: #28c76f; color: #fff; }
.paused-item__note-save:hover:not(:disabled) { background: #24b363; }
.paused-item__note-cancel { background: #f3f2f7; color: #5e5873; }
.paused-item__note-cancel:hover:not(:disabled) { background: #e9e8ef; }
.paused-item__note-save:disabled,
.paused-item__note-cancel:disabled { opacity: 0.6; cursor: not-allowed; }

.pause-modal {
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.pause-modal__info {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid #ebe9f1;
}
.pause-modal__sku {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    color: #5e5873;
}
.pause-modal__color {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    color: #fff;
    background: #7367f0;
    padding: 3px 8px;
    border-radius: 999px;
}
.pause-modal__label {
    font-size: 12px;
    font-weight: 600;
    color: #5e5873;
    margin: 0;
}
.pause-modal__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
}
.pause-modal__cancel,
.pause-modal__save {
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;
}
.pause-modal__cancel {
    background: #f3f2f7;
    color: #5e5873;
}
.pause-modal__cancel:hover:not(:disabled) {
    background: #e9e8ef;
}
.pause-modal__save {
    background: #ea5455;
    color: #fff;
}
.pause-modal__save:hover:not(:disabled) {
    background: #d84546;
}
.pause-modal__save:disabled,
.pause-modal__cancel:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>

<style lang="scss">
// ══════════ DARK MODE ══════════ (sin scoped: .dark-layout está en body)
// Paleta negra, misma que el listado de pedidos Vero
.dark-layout .pc {
    background: #161616;
    color: #d0d2d6;

    .pc-dash-header { background: #1f1f1f; border-color: #2c2c2c; box-shadow: none; }
    .pc-title { color: #e4e6eb; }
    .pc-stat-pill { background: #161616; border-color: #2c2c2c; color: #8e9099; }
    .pc-stat-pill strong { color: #e4e6eb; }
    .pc-stat-pill--cta, .pc-stat-pill--cta strong { color: #F97316; }
    .pc-stat-pill--success, .pc-stat-pill--success strong { color: #10B981; }
    .pc-stat-pill--paused, .pc-stat-pill--paused strong { color: #F59E0B; }
    .pc-search-box .pc-search-input { background: #1f1f1f; border-color: #2c2c2c; color: #d0d2d6; }
    .pc-resultado-card { background: #1f1f1f; border-color: #2c2c2c; }
    .pc-resultado-head strong,
    .pc-resultado-item .pc-item-sku { color: #e4e6eb; }
    .pc-resultado-item { border-top-color: #2c2c2c; }

    // Panel de pausados
    .paused-panel { background: #1f1f1f; }
    .paused-panel__title { color: #F59E0B; }
    .paused-panel__close { color: #8e9099; }
    .paused-item + .paused-item { border-top-color: #2c2c2c; }
    .paused-item__sku { color: #e4e6eb; }
    .paused-item__progress { color: #b4b7bd; }
    .paused-item__note,
    .paused-item__note-edit { color: #F59E0B; }
    .paused-item__note-input { background: #161616; border-color: #2c2c2c; color: #d0d2d6; }
    .paused-item__note-cancel { background: #2c2c2c; color: #b4b7bd; }
    .paused-item__note-cancel:hover:not(:disabled) { background: #3a3a3a; }

    .pedido-card { background: #161616; border-color: #2c2c2c; }
    .pedido-card:hover { border-color: #3B82F6; box-shadow: none; }
    .pedido-card.pedido-card-selected { background: rgba(59, 130, 246, 0.08); border-color: #3B82F6; }
    .pedido-card-codigo,
    .pedido-stat-value:not(.pedido-stat-value--cta) { color: #e4e6eb; }
    .pedido-card-meta { color: #8e9099; }
    .pedido-card-stats { border-color: #2c2c2c; }
    .pedido-card-estado-pill.pcs-light { background: #2c2c2c; color: #b4b7bd; }
    .pedido-progress-track { background: #2c2c2c; }
    .pedido-comentario-banner__text { color: #e4e6eb; }

    .table-progreso th { background: #161616 !important; color: #b4b7bd; }
    // Filas alternas y columna fija (Producto): bootstrap-vue le pone fondo claro
    .table-progreso tbody tr { background: #1f1f1f !important; }
    .table-progreso tbody tr:nth-child(even) { background: #242424 !important; }
    .table-progreso tbody .b-table-sticky-column { background: inherit !important; color: #e4e6eb; }
    .table-progreso .progress { background: #2c2c2c; }
    .btn-pause { color: #8e9099; }
}

// Modales (se montan en body, fuera de .pc)
.dark-layout .modal {
    .modal-content .comentario-texto { background: #161616; border-color: #2c2c2c; color: #d0d2d6; }
    .pause-modal .pause-modal__info { border-bottom-color: #2c2c2c; }
    .pause-modal .pause-modal__sku,
    .pause-modal .pause-modal__label { color: #d0d2d6; }
    .pause-modal .pause-modal__cancel { background: #2c2c2c; color: #b4b7bd; }
    .pause-modal .pause-modal__cancel:hover:not(:disabled) { background: #3a3a3a; }
}
</style>
